import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'src/utils/axios';
import ReactDOM from 'react-dom/client';
import ConnectChatWidget from 'src/components/ConnectChat/ConnectChatWidget';
import ChatLauncher from 'src/components/ConnectChat/ChatLauncher';

interface ChatState {
  /**
   * Open the floating chat corner.
   *
   * With no intent, shows the launcher so the user picks. With an intent, skips
   * the launcher and starts that flow directly — used by the "Report Issue" and
   * "Start Chat" buttons in the Help and Resources hub.
   */
  openChat: (intent?: 'agent' | 'report_issue') => void;
  isLoaded: boolean;
  error: string | null;
  /**
   * True while a chat session is being provisioned (ChatJS download + the
   * start_chat round trip). Surfaced so callers outside the floating corner —
   * e.g. the Help and Resources escalate buttons — can show progress instead
   * of appearing frozen for the ~1s this takes.
   */
  loading: boolean;
  // True whenever the launcher, live widget, or minimized pill occupies
  // the floating chat corner — FloatingChatButton hides its own FAB in
  // this case so there's never two floating elements stacked on top of
  // each other.
  hasActiveChat: boolean;
}

let chatJsLoaded = false;
let chatJsLoading: Promise<void> | null = null;

/**
 * Warm the ChatJS bundle before the user clicks.
 *
 * `loadChatJs` is module-scoped and idempotent, so calling this early is free
 * and removes the script download from the click path entirely — that fetch is
 * the single largest contributor to open latency on a cold page.
 */
export function preloadChatJs(): void {
  loadChatJs().catch(() => undefined);
}

function loadChatJs(): Promise<void> {
  if (chatJsLoaded) {
    return Promise.resolve();
  }
  if (chatJsLoading) {
    return chatJsLoading;
  }

  chatJsLoading = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector('script[data-chatjs]');
    if (existingScript) {
      chatJsLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = '/amazon-connect-chatjs.js';
    script.setAttribute('data-chatjs', '1');
    script.onload = () => {
      chatJsLoaded = true;
      resolve();
    };
    script.onerror = () => {
      chatJsLoading = null;
      reject(new Error('Failed to load ChatJS'));
    };
    document.head.appendChild(script);
  });

  return chatJsLoading;
}

/**
 * Hook that picks the chat system based on CONNECT_CHAT_ENABLED from Redux.
 * Flag ON  → Connect Chat (ChatLauncher → ConnectChatWidget)
 * Flag OFF → HubSpot (existing behavior)
 *
 * Entry point is always the launcher (recent history + "Start new
 * conversation") — a Connect session is only ever created when the user
 * explicitly clicks that button, never just from opening the chat bubble.
 */
export function useConnectChat(): ChatState {
  const connectChatEnabled = useSelector(
    (state: any) => state.featureFlags?.flags?.CONNECT_CHAT_ENABLED
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasActiveChat, setHasActiveChat] = useState(false);
  const widgetRootRef = useRef<{ root: any; container: HTMLDivElement } | null>(
    null
  );
  // Set right before swapping the shared root's content between Launcher
  // and Widget (in either direction). React unmounts the outgoing tree as
  // part of that same render, firing ConnectChatWidget's onUnmount — this
  // flag tells that handler "this is an intentional swap, not a real
  // teardown" so it doesn't rip the container out from under the incoming
  // component.
  const swappingRef = useRef(false);

  // When Redux flags haven't loaded yet (undefined), default to Connect.
  // This is safe: Thor's start_chat endpoint enforces the flag server-side.
  // If the flag is truly OFF, Thor returns 503 and the hook falls back to HubSpot.
  const mode: 'hubspot' | 'connect' =
    connectChatEnabled === false ? 'hubspot' : 'connect';
  const isLoaded = connectChatEnabled !== undefined;

  const closeSharedRoot = useCallback((): void => {
    if (!widgetRootRef.current) {
      return;
    }
    try {
      widgetRootRef.current.root.unmount();
    } catch {}
    if (widgetRootRef.current.container.parentNode) {
      widgetRootRef.current.container.parentNode.removeChild(
        widgetRootRef.current.container
      );
    }
    widgetRootRef.current = null;
    setHasActiveChat(false);
  }, []);

  // Cleanup on component unmount (e.g. navigating away mid-chat)
  useEffect(() => {
    return () => {
      closeSharedRoot();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showLauncher = useCallback((): void => {
    if (!widgetRootRef.current) {
      const container = document.createElement('div');
      container.id = 'connect-chat-widget-container';
      document.body.appendChild(container);
      const root = ReactDOM.createRoot(container);
      widgetRootRef.current = { root, container };
    }
    setHasActiveChat(true);
    widgetRootRef.current.root.render(
      <ChatLauncher
        onStartNew={startConnectChatRef.current}
        onClose={closeSharedRoot}
      />
    );
  }, [closeSharedRoot]);

  // startConnectChat needs to call showLauncher (on end) and showLauncher
  // needs to pass startConnectChat to the launcher — a genuine mutual
  // dependency. A ref breaks the cycle without either function being
  // stale: showLauncher always reads whatever startConnectChat currently
  // is at render time it's invoked from.
  const startConnectChatRef = useRef<
    (intent?: 'agent' | 'report_issue') => Promise<void>
  >(async () => undefined);

  // Throws on failure (except the 503→HubSpot fallback, which resolves
  // normally) so callers — namely ChatLauncher's "Start new conversation"
  // button — can show an inline error instead of silently doing nothing.
  // intent: 'agent' (default) routes to the live-agent queue. 'report_issue'
  // skips the queue entirely — the Connect flow's check_intent block routes
  // it straight to a short ticket-creation exchange (see ConnectChatWidget's
  // Submit Ticket control), never exposing it to an agent.
  const startConnectChat = useCallback(
    async (intent: 'agent' | 'report_issue' = 'agent'): Promise<void> => {
      if (loading || mode !== 'connect') {
        return;
      }
      setLoading(true);
      setError(null);

      try {
        try {
          await loadChatJs();
        } catch {
          throw new Error('chatjs-load-failed');
        }

        const res = await axios.post('/connect/start_chat/', {
          pageContext: window.location.pathname,
          intent,
        });

        const { contactId, participantId, participantToken } = res.data;

        const handleEnd = async (transcript: any[]): Promise<void> => {
          try {
            await axios.patch(`/connect/chat_session/${contactId}/finalize/`, {
              transcript,
              disconnect_timestamp: new Date().toISOString(),
              disconnect_reason: 'CustomerInitiated',
            });
          } catch {}
          // Swap back to the launcher (fresh history fetch) in the same
          // root, rather than tearing the whole thing down — satisfies
          // "after chat ends, show updated history + new button again"
          // without requiring the user to reopen the chat bubble.
          swappingRef.current = true;
          showLauncher();
        };

        // Fires when the widget instance unmounts, for any reason. If this
        // is the intentional end→Launcher swap (handleEnd already set the
        // flag and re-rendered), skip teardown — the container now belongs
        // to the Launcher. Otherwise (an unexpected crash) tear down for
        // real so the chat bubble can be clicked again for a clean start.
        const handleUnmount = (): void => {
          if (swappingRef.current) {
            swappingRef.current = false;
            return;
          }
          closeSharedRoot();
        };

        // Reuses the launcher's shared root/container (the common case: user
        // clicked "Start new conversation" in ChatLauncher, which is already
        // rendered into widgetRootRef).
        if (!widgetRootRef.current) {
          const container = document.createElement('div');
          container.id = 'connect-chat-widget-container';
          document.body.appendChild(container);
          const root = ReactDOM.createRoot(container);
          widgetRootRef.current = { root, container };
        }

        setHasActiveChat(true);
        widgetRootRef.current.root.render(
          <ConnectChatWidget
            contactId={contactId}
            participantId={participantId}
            participantToken={participantToken}
            region="us-east-1"
            intent={intent}
            onEnd={handleEnd}
            onUnmount={handleUnmount}
          />
        );
      } catch (e: any) {
        if (e?.response?.status === 503) {
          // Flag flipped OFF between check and call. Fall back to HubSpot —
          // close whatever launcher/widget root is showing since the user is
          // now looking at the HubSpot widget instead.
          closeSharedRoot();
          if ((window as any).HubSpotConversations?.widget) {
            (window as any).HubSpotConversations.widget.open();
          }
          setLoading(false);
          return;
        }
        setError('Chat unavailable — email support@prontohousing.com');
        setLoading(false);
        throw e;
      }

      setLoading(false);
    },
    [loading, mode, closeSharedRoot, showLauncher]
  );

  startConnectChatRef.current = startConnectChat;

  const openChat = useCallback(
    (intent?: 'agent' | 'report_issue') => {
      // Already showing widget or launcher? Do nothing.
      if (widgetRootRef.current) {
        return;
      }

      if (mode !== 'connect') {
        if ((window as any).HubSpotConversations?.widget) {
          (window as any).HubSpotConversations.widget.open();
        }
        return;
      }

      // An explicit intent skips the launcher's choice screen and starts that
      // flow straight away. Errors surface through the hook's `error` state,
      // which FloatingChatButton renders in its tooltip.
      if (intent) {
        startConnectChatRef.current(intent).catch(() => undefined);
        return;
      }

      showLauncher();
    },
    [mode, showLauncher]
  );

  return { openChat, isLoaded, error, hasActiveChat, loading };
}
